import { useCallback, useState } from "react";

import { Icon } from "@blueprintjs/core";
import { addDays, format } from "date-fns";
import { Calendar } from "lucide-react";
import { useMediaQuery } from "react-responsive";

import { isXsQuery } from "@/components/layout/Media";
import { dateStringToDate } from "@/components/pages_logged_in/schedule/timeslots/date-string-to-date";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/utils/datetime";

import { CalendarWidget } from "./CalendarWidget";
import { EmptyTimeslots } from "./EmptyTimeslots";
import { TimeslotSelector } from "./TimeslotSelector";
import { TimezoneSelector } from "./TimezoneSelector";

interface StackedTimeslotLayoutProps {
  loading: boolean;
  requiresBookingApproval: boolean;
  isViewOnly: boolean;
  timeslots: string[];
  unavailableTimeslots: string[];
  dateString: string;
  onSelectTimeslots: (timeslots: string[]) => void;
  selectedTimeslots: string[];
  setDateString: (dateString: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  availableDates: Record<string, boolean>;
  preferredTimeslots: string[];
  onPreferredChange: (preferredTimeslots: string[]) => void;
}

export function StackedTimeslotLayout({
  loading,
  requiresBookingApproval,
  isViewOnly,
  timeslots,
  unavailableTimeslots,
  dateString,
  selectedTimeslots,
  setDateString,
  timezone,
  setTimezone,
  availableDates,
  onSelectTimeslots,
  preferredTimeslots,
  onPreferredChange,
}: StackedTimeslotLayoutProps) {
  const [displayMode, setDisplayMode] = useState<"time" | "calendar">("time");

  const toggleDisplayMode = () => {
    setDisplayMode(displayMode === "time" ? "calendar" : "time");
  };

  const nextDay = useCallback(
    (numberDay: number) => {
      if (dateString) {
        const [month, day, year] = dateString.split("-").map(Number);
        const date = new Date(year, month - 1, day);
        const newDate = addDays(date, numberDay);
        const newDateString = format(newDate, "MM-dd-yyyy")!;
        setDateString(newDateString);
      }
    },
    [dateString, timezone],
  );

  const isXs = useMediaQuery(isXsQuery);

  return (
    <>
      <div className="text-lg font-semibold mb-4">Schedule your session</div>
      <div className="rounded-lg border border-gray-300 bg-white p-6">
        {!dateString && !loading && <EmptyTimeslots adjustDate={false} />}

        <div className="date-label flex items-center justify-between">
          <button
            onClick={() => nextDay(-1)}
            className="cursor-pointer rounded"
          >
            <Icon icon="arrow-left" className="text-gray-700" />
          </button>
          <Button
            onClick={toggleDisplayMode}
            intent="secondary"
            variant="minimal"
            size="sm"
            disabled={isXs}
          >
            <Calendar className="size-4" />
            <div className="text-xs md:text-base text-blue-600">
              {formatDate(
                dateStringToDate(dateString, timezone),
                "eeee, MMMM d z",
                timezone,
              )}
            </div>
          </Button>

          <button onClick={() => nextDay(1)} className="cursor-pointer rounded">
            <Icon icon="arrow-right" className="text-gray-700" />
          </button>
        </div>

        {displayMode === "time" ? (
          <TimeslotSelector
            requiresBookingApproval={requiresBookingApproval}
            isViewOnly={isViewOnly}
            timeslots={timeslots}
            unavailableTimeslots={unavailableTimeslots}
            loading={loading}
            timezone={timezone}
            onChange={(timeslots) => {
              onSelectTimeslots(timeslots);
            }}
            selectedTimeslots={selectedTimeslots}
            preferredTimeslots={preferredTimeslots}
            onPreferredChange={onPreferredChange}
          />
        ) : (
          <div className="mx-16 mt-4">
            <CalendarWidget
              dateString={dateString}
              setDateString={setDateString}
              availableDates={availableDates}
              toggleDisplayMode={toggleDisplayMode}
            />
          </div>
        )}

        <div className="w-full mt-4">
          <TimezoneSelector
            value={timezone}
            onChange={(tz) => tz && setTimezone(tz.value)}
          />
        </div>
      </div>
    </>
  );
}
